import { OUTREACH_INBOX_QUERY } from "@lib/graphql/outreach";
import { useOutreachListChannel } from "@lib/hooks/useOutreachListChannel";
import { useCallback, useState } from "react";
import { useQuery } from "urql";
import { InboxDrawer } from "./InboxDrawer";
import { InboxRow } from "./InboxRow";
import type { InboxBucket, InboxItem } from "./types";

interface Props {
  programmeId: string | null;
}

const BUCKETS: Array<{ id: InboxBucket; label: string }> = [
  { id: "READY_TO_SEND", label: "Ready to send" },
  { id: "AWAITING_ACCEPT", label: "Sent — awaiting accept" },
  { id: "ACCEPTED_NEEDS_FIRST_MESSAGE", label: "Accepted — needs first message" },
  { id: "REPLIED_NEEDS_FOLLOWUP", label: "Replied — needs follow-up" },
  { id: "SNOOZED", label: "Snoozed" },
];

export function InboxTab({ programmeId }: Props) {
  const [bucket, setBucket] = useState<InboxBucket>("READY_TO_SEND");
  const [drawerItem, setDrawerItem] = useState<InboxItem | null>(null);

  const [{ data, fetching }, reexecute] = useQuery({
    query: OUTREACH_INBOX_QUERY,
    variables: { bucket, programmeId },
  });
  const refresh = useCallback(() => reexecute({ requestPolicy: "network-only" }), [reexecute]);
  useOutreachListChannel(refresh);

  const items: InboxItem[] = data?.outreachInbox ?? [];

  return (
    <div className="flex gap-4">
      <nav className="w-56 shrink-0">
        <ul className="space-y-1">
          {BUCKETS.map((b) => {
            const active = bucket === b.id;
            return (
              <li key={b.id}>
                <button
                  onClick={() => setBucket(b.id)}
                  className={`block w-full rounded-md px-3 py-2 text-left text-sm ${
                    active
                      ? "bg-accent/10 font-medium text-text-primary"
                      : "text-text-secondary hover:bg-card-hover"
                  }`}
                >
                  {b.label}
                </button>
              </li>
            );
          })}
        </ul>
      </nav>

      <div className="min-w-0 flex-1">
        {fetching && items.length === 0 ? (
          <p className="text-sm text-text-muted">Loading inbox…</p>
        ) : items.length === 0 ? (
          <div className="rounded-lg border-2 border-dashed border-border-dashed p-8 text-center">
            <p className="text-sm text-text-muted">Nothing in this bucket right now.</p>
          </div>
        ) : (
          <div className="space-y-3">
            {items.map((item) => (
              <InboxRow
                key={item.id}
                item={item}
                bucket={bucket}
                onChanged={refresh}
                onOpen={setDrawerItem}
              />
            ))}
          </div>
        )}
      </div>

      <InboxDrawer
        open={!!drawerItem}
        item={drawerItem}
        onClose={() => setDrawerItem(null)}
        onChanged={refresh}
      />
    </div>
  );
}
